import React, { useCallback, useRef, useState } from 'react';

import { Calendar } from '@gravity-ui/icons';
import { Button, Card, Checkbox, Flex, Icon, Popup, RadioButton, Select, TextArea, TextInput } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { devicesTestingApi } from 'client/api';
import { getOnlineDevicesTestingNoteText } from 'client/blocks/TestingDateSelection/text';
import { AliceAppActionButton } from 'client/blocks-redesign/AliceAppActionButton/AliceAppActionButton';
import { useCurrentAliceApp } from 'client/hooks/useCurrentAliceApp';
import { useFetchingHandler } from 'client/hooks/useFetchingHandler';
import { useFormSettings } from 'client/hooks/useFormSettings';
import { moment } from 'client/lib/moment';
import {
    DevicesTestingFormSettings,
    DevicesTestingType,
    Range,
    serializeDevicesTestingOptions,
} from 'client/model/devicesTestingForm';
import { getIsYangoRequest } from 'client/utils';
import { formatDevicesTestingRange, generateTestingHours } from 'client/utils/time';
import * as keyset from 'i18n/DraftControlPanel';

import styles from './SmartHomeTestingDate.module.scss';

const t = i18n(keyset);

interface Props {
    afterSave?: () => Promise<void>;
    onCancel?: () => void;
    initialSettings: DevicesTestingFormSettings;
    disabled?: boolean;
}

interface TestingDateSelectProps {
    onSelect: (_range: Range) => void;
    range?: Range;
    disabled?: boolean;
}

const generateInputName = (name: string) => {
    return `paskills-testing_date-${name}`;
};

const TestingDateSelect: React.FC<TestingDateSelectProps> = ({ onSelect, range, disabled }) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const [popupIsVisible, setPopupIsVisible] = useState(false);

    const [testingDateRanges, testingDateOrder] = generateTestingHours(moment());

    return (
        <>
            <div ref={containerRef} className={styles.smartHomeTestingDateForm__element}>
                <TextInput
                    name={generateInputName('date')}
                    className={styles.smartHomeTestingDateForm__element}
                    endContent={<Icon data={Calendar} />}
                    placeholder={`* ${t('date-time')}`}
                    value={range ? formatDevicesTestingRange(range, true) : ''}
                    onFocus={() => !disabled && setPopupIsVisible(true)}
                    autoComplete="none"
                    disabled={disabled}
                />
            </div>
            <Popup
                open={popupIsVisible && !disabled}
                anchorRef={containerRef}
                onOutsideClick={() => setPopupIsVisible(false)}
                placement="bottom-end"
            >
                <div>
                    {testingDateOrder.map((formattedDate, i) => {
                        const ranges = testingDateRanges[formattedDate];

                        return (
                            <div key={i}>
                                <div className={styles.smartHomeTestingDateForm__popupElement}>{formattedDate}</div>
                                {ranges.map((r, j) => {
                                    const onClick = () => {
                                        if (disabled) return;
                                        onSelect(r);
                                        setPopupIsVisible(false);
                                    };

                                    return (
                                        <div
                                            key={j}
                                            className={styles.smartHomeTestingDateForm__popupElement}
                                            onClick={onClick}
                                        >
                                            {formatDevicesTestingRange(r)}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </Popup>
        </>
    );
};

export const SmartHomeTestingDateForm: React.FC<Props> = ({ afterSave, onCancel, initialSettings, disabled }) => {
    const skill = useCurrentAliceApp()!;
    const [isChanged, setIsChanges] = useState(false);
    const { settings, onChange } = useFormSettings({
        defaultSettings: initialSettings,
        registerChanges: setIsChanges,
    });

    const [isDevicesTransferConfirmed, setIsDevicesTransferConfirmed] = useState(false);

    const isFormSettingsSet =
        settings.login &&
        settings.password &&
        settings.selectedDateRange &&
        (settings.type === 'skype' ? settings.skypeLogin : true);

    const isSavingAllowed = isFormSettingsSet && isChanged;

    const [onSave, savingState] = useFetchingHandler({
        handler: async () => {
            const res = await devicesTestingApi.saveDevicesTestingOptions(
                skill.id,
                skill.channel,
                serializeDevicesTestingOptions(settings),
            );
            await afterSave?.();

            return res;
        },
    });

    const isSaving = savingState === 'loading';

    const onLangChange = useCallback((langs: string[]) => onChange('lang')(langs?.[0]), [onChange]);

    const langOptions = [
        { value: 'ru', content: t('rus') },
        { value: 'en', content: t('eng') },
    ];

    return (
        <div>
            <div className={styles.smartHomeTestingDateForm__fields}>
                <RadioButton
                    name="type"
                    value={settings.type}
                    onChange={(e) => onChange('type')(e.target.value as DevicesTestingType)}
                    disabled={disabled}
                >
                    <RadioButton.Option value="zoom">{t('meeting-zoom')}</RadioButton.Option>
                    <RadioButton.Option value="skype">{t('meeting-skype')}</RadioButton.Option>
                </RadioButton>

                {settings.type === 'zoom' || settings.type === 'skype' ? (
                    <>
                        <TestingDateSelect
                            range={settings.selectedDateRange}
                            onSelect={onChange('selectedDateRange')}
                            disabled={disabled}
                        />
                        <TextInput
                            name={generateInputName('provider_login')}
                            className={styles.smartHomeTestingDateForm__element}
                            value={settings.login}
                            onUpdate={onChange('login')}
                            autoComplete="none"
                            placeholder={`* ${t('meeting-provider-login')}`}
                            disabled={disabled}
                        />

                        {/* Обертка в виде формы нужна, чтобы предотвратить автоподстановку доменных паролей*/}
                        <form
                            className={styles.smartHomeTestingDateForm__element}
                            onSubmit={(e) => e.preventDefault()}
                            autoComplete="off"
                        >
                            <TextInput
                                name={generateInputName('provider_password')}
                                className={styles.smartHomeTestingDateForm__element}
                                value={settings.password}
                                type="password"
                                onUpdate={onChange('password')}
                                autoComplete="none"
                                placeholder={`* ${t('meeting-provider-password')}`}
                                disabled={disabled}
                            />
                        </form>
                        {settings.type === 'skype' && (
                            <TextInput
                                name={generateInputName('skype_login')}
                                className={styles.smartHomeTestingDateForm__element}
                                value={settings.skypeLogin}
                                onUpdate={onChange('skypeLogin')}
                                placeholder={`* ${t('meeting-skype-login')}`}
                                disabled={disabled}
                            />
                        )}
                        <TextArea
                            name={generateInputName('comment')}
                            className={styles.smartHomeTestingDateForm__element}
                            value={settings.comment!}
                            rows={2}
                            onUpdate={onChange('comment')}
                            autoComplete="none"
                            placeholder={t('comment')}
                            disabled={disabled}
                        />

                        <div className={styles.smartHomeTestingDateForm__element}>
                            <div className={styles.smartHomeTestingDateForm__label}>{t('meeting-choose-lang')}</div>
                            <Select
                                className={styles.smartHomeTestingDateForm__element}
                                value={[settings.lang]}
                                onUpdate={onLangChange}
                                placeholder={t('lang')}
                                options={langOptions}
                                disabled={disabled}
                                title={t('meeting-choose-lang')}
                            />
                        </div>

                        <Card className={styles.smartHomeTestingDateCard} theme="warning">
                            {getOnlineDevicesTestingNoteText()}
                        </Card>
                    </>
                ) : (
                    <Checkbox
                        className={styles.smartHomeTestingDateForm__element}
                        checked={isDevicesTransferConfirmed}
                        onChange={() => setIsDevicesTransferConfirmed(!isDevicesTransferConfirmed)}
                        disabled={disabled}
                    >
                        {t(getIsYangoRequest() ? 'meeting-devices-sent-yango' : 'meeting-devices-sent-yandex')}
                    </Checkbox>
                )}
            </div>
            <Flex gap={2}>
                <AliceAppActionButton
                    action={async () => {
                        await onSave();
                    }}
                    successLabel=""
                    disabled={disabled || !isSavingAllowed}
                >
                    {t('save')}
                </AliceAppActionButton>
                {onCancel && (
                    <Button onClick={onCancel} disabled={disabled || isSaving}>
                        {t('cancel')}
                    </Button>
                )}
            </Flex>
        </div>
    );
};
